import React, {useRef, useState} from 'react';
import {type TabNode, Actions} from 'flexlayout-react';
import {usePositionOverlay} from '@qep/shared/src/utils/use-position-overlay';
import {Portal} from '@qep/shared/src/components/Portal/Portal';
import _ from 'lodash';
import ChainLink from 'jsx:./chainlink.svg';
import {DefaultLinkerGroupKeys, type LinkerGroups} from '../../communication/types.js';
import {getContainer} from '../../utils/utils.js';
import './linker.scss';
import useLinkerGroups from './link-groups-hook.js';

const linkerClasses = {
  customDropdownMenu: 'browser-linker-dropdown-menu',
  linkGroup: 'browser-linker-link-group',
  linkIcon: 'browser-linker-link-icon',
  overlay: 'browser-linker-overlay',
};

const iconClasses = [
  'browser-linker-icon-1',
  'browser-linker-icon-2',
  'browser-linker-icon-3',
];

const tabIconClasses = [
  'browser-linker-tab-icon-1',
  'browser-linker-tab-icon-2',
  'browser-linker-tab-icon-3',
];

/*
    Dynamically creates linker groups from types making it easier to change defaults in the future.
    But also allows for linker group names to be more globally available.
*/
const linkerGroupList: LinkerGroups = {};
for (const [i, key] of Object.keys(DefaultLinkerGroupKeys).entries()) {
  const groupName = `${key.slice(0, 1)}${key.slice(1).replace('_', ' ').toLowerCase()}`;
  const groupKey = DefaultLinkerGroupKeys[key as keyof typeof DefaultLinkerGroupKeys];
  linkerGroupList[groupKey] = {
    icon: iconClasses[i],
    tabIcon: tabIconClasses[i],
    name: groupName,
  };
}

type LinkerProps = {
  node: TabNode;
  // Replace 'any' with the appropriate type if known
};

export default function Linker({node}: LinkerProps) {
  const [id, SetId] = useState(Math.random().toString(36).slice(7));
  // const [linkerGroups, setLinkerGroups] = useState<string[]>([]);
  const linkerGroups = useLinkerGroups(node);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLDivElement>(null);
  const [overlayPos, recalculate] = usePositionOverlay(buttonRef, dropdownRef);
  const [showDropdown, setShowDropdown] = useState(false);

  const handleDropdownClick = (event: React.MouseEvent<HTMLElement> | React.FocusEvent) => {
    recalculate();
    setShowDropdown(!showDropdown);
  };

  const getLinkerIcon = () => {
    if (linkerGroups.length === 0) {
      return <ChainLink id={`icon_${id}`} className={linkerClasses.linkIcon} />;
    }

    return linkerGroups.map(groupId => <span key={groupId} className={linkerGroupList[groupId].tabIcon}></span>);
  };

  const getGroupIndex = (groupId: string) => linkerGroups.indexOf(groupId);

  const setLinkerGroup = async (groupId: string) => {
    const groupIndex = getGroupIndex(groupId);
    const newLinkerGroups = await (groupIndex === -1 ? getContainer().linker.registerLink(groupId, node.getId()) : getContainer().linker.deRegisterLink(groupId, node.getId()));
    const myConfigObject = node.getConfig() as unknown as {linkerGroups: string[]};
    myConfigObject.linkerGroups = newLinkerGroups;
    node.getModel().doAction(Actions.updateNodeAttributes(node.getId(), {config: myConfigObject}));
  };

  return (
    <div ref={buttonRef} style={{position: 'relative', zIndex: 1050}} data-testid={`linker-button-${node.getName()}`}>
      <span onClick={handleDropdownClick} id={id}>{getLinkerIcon()}</span>
      {showDropdown && (
        <Portal
          outsideClickOmitElements={[dropdownRef, buttonRef]}
          className={linkerClasses.overlay}
          style={{top: `${overlayPos.top}px`, left: `${overlayPos.left}px`}}
          onOutsideClick={() => {
            setShowDropdown(false);
          }}>
          <div
            data-testid='linker-menu'
            ref={dropdownRef}
            className={linkerClasses.customDropdownMenu}>
            {Object.entries(linkerGroupList).map(([key, value]) => (
              <div key={key}
                className={linkerClasses.linkGroup}
                onClick={async () => {
                  await setLinkerGroup(key);
                  setShowDropdown(false);
                }}>
                <span className={value.icon}></span>
                {value.name}
              </div>
            ))}
          </div>
        </Portal>
      )}
    </div>
  );
}
